import { ArrowLeftIcon, PrinterIcon } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useJob } from '@/features/jobs/queries';
import { useBranding } from '@/lib/branding';
import { useKeywordQr, useKeywords } from './queries';

/**
 * Printable gate sign: "Text JOIN to (407) 555-0100" with a QR code that opens the messaging app
 * pre-filled, plus the consent disclosure. Rendered without the app shell so it prints cleanly.
 */
export function KeywordSignPage() {
  const { jobId = '', keywordId = '' } = useParams();
  const job = useJob(jobId);
  const keywords = useKeywords(jobId);
  const qr = useKeywordQr(jobId, keywordId || null, 900);
  const { branding } = useBranding();

  const k = keywords.data?.items.find((x) => x.id === keywordId);
  const number = keywords.data?.number ?? null;
  const consent = (k?.settings.consentText ?? keywords.data?.policy.joinDefaults.consentText ?? '').replace(/\{\{\s*job\.name\s*\}\}/g, job.data?.job.name ?? '');

  return (
    <div className="bg-background min-h-dvh">
      <div className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-6 py-4 print:hidden">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/jobs/${jobId}/keywords`}>
            <ArrowLeftIcon /> Back to keywords
          </Link>
        </Button>
        <Button onClick={() => window.print()}>
          <PrinterIcon /> Print
        </Button>
      </div>

      <main className="mx-auto max-w-3xl px-6 pb-12 print:max-w-none print:px-0">
        <div className="border-4 border-foreground p-10 text-center print:border-8">
          <div className="flex items-center justify-center gap-3">
            {branding.logoUrl ? <img src={branding.logoUrl} alt={branding.tenantName} className="max-h-12" /> : <Logo />}
          </div>
          <p className="text-muted-foreground mt-6 text-sm font-medium tracking-[0.3em] uppercase">{branding.tenantName}</p>
          <h1 className="font-display mt-2 text-4xl leading-tight print:text-5xl">{job.data?.job.name ?? <Skeleton className="mx-auto h-10 w-64" />}</h1>
          <p className="mt-6 text-2xl print:text-3xl">Get site updates by text.</p>

          <div className="mt-8 grid items-center gap-8 sm:grid-cols-[1fr_auto]">
            <div className="text-left">
              <p className="text-muted-foreground text-lg">Text</p>
              <p className="font-mono text-6xl font-bold tracking-wide print:text-7xl">{k?.keyword ?? '…'}</p>
              <p className="text-muted-foreground mt-3 text-lg">to</p>
              <p className="text-4xl font-semibold tabular-nums print:text-5xl">{number?.display ?? '—'}</p>
              <p className="text-muted-foreground mt-4 text-base">Or scan the code with your phone camera.</p>
            </div>
            <div className="mx-auto flex items-center justify-center border-2 border-foreground bg-white p-3" style={{ width: 260, height: 260 }}>
              {qr.data ? <div className="[&>svg]:size-full" dangerouslySetInnerHTML={{ __html: qr.data }} /> : <Skeleton className="size-full" />}
            </div>
          </div>

          <div className="mt-10 border-t pt-6 text-left">
            <p className="text-sm leading-relaxed">{consent}</p>
            <p className="text-muted-foreground mt-2 text-xs">Standard message and data rates may apply. Reply STOP at any time to stop receiving texts, or HELP for help. Your number is used only for this job&apos;s updates.</p>
          </div>
        </div>
      </main>
    </div>
  );
}
